import { ref, onMounted, onUnmounted, computed } from 'vue'

// Global state for keyboard shortcuts
const activeShortcuts = new Map()
const shortcutHistory = []
const maxHistoryLength = 50

// Key mapping for special keys
const keyMap = {
  ' ': 'space',
  'Control': 'ctrl',
  'Meta': 'cmd',
  'Alt': 'alt',
  'Shift': 'shift',
  'Enter': 'enter',
  'Escape': 'esc',
  'Tab': 'tab',
  'Backspace': 'backspace',
  'Delete': 'del',
  'ArrowUp': 'up',
  'ArrowDown': 'down',
  'ArrowLeft': 'left',
  'ArrowRight': 'right'
}

// Convert event to shortcut string
const eventToShortcut = (event) => {
  const parts = []
  
  if (event.ctrlKey) parts.push('ctrl')
  if (event.metaKey) parts.push('cmd')
  if (event.altKey) parts.push('alt')
  if (event.shiftKey) parts.push('shift')
  
  const key = keyMap[event.key] || event.key.toLowerCase()
  if (key && key !== 'ctrl' && key !== 'cmd' && key !== 'alt' && key !== 'shift') {
    parts.push(key)
  }
  
  return parts.join('+')
}

// Default shortcuts configuration
const defaultShortcuts = {
  'search': 'ctrl+k',
  'help': '?',
  'darkMode': 'ctrl+shift+d',
  'toggleSidebar': 'ctrl+b',
  'settings': 'ctrl+,',
  'save': 'ctrl+s',
  'delete': 'del',
  'closeModal': 'esc',
  'refresh': 'f5'
}

export function useKeyboardShortcuts() {
  const isRecording = ref(false)
  const recordingFor = ref('')
  const lastPressed = ref('')
  const conflictingShortcuts = ref([])
  
  const shortcuts = computed(() => defaultShortcuts)
  
  // Find action that matches the shortcut
  const findMatchingAction = (shortcut) => {
    for (const [action, actionShortcut] of Object.entries(shortcuts.value)) {
      if (actionShortcut === shortcut) {
        return action
      }
    }
    return null
  }
  
  // Add shortcut to history
  const addToHistory = (action, shortcut) => {
    shortcutHistory.unshift({
      action,
      shortcut,
      timestamp: Date.now()
    })
    
    if (shortcutHistory.length > maxHistoryLength) {
      shortcutHistory.splice(maxHistoryLength)
    }
  }
  
  // Global keyboard event handler
  const handleKeyDown = (event) => {
    // Skip if user is typing in an input field
    const activeElement = document.activeElement
    if (activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.contentEditable === 'true'
    )) {
      // Only allow specific shortcuts in input fields
      const allowedInInput = ['esc', 'ctrl+s', 'ctrl+z', 'ctrl+y']
      const shortcut = eventToShortcut(event)
      if (!allowedInInput.includes(shortcut)) {
        return
      }
    }
    
    const shortcut = eventToShortcut(event)
    lastPressed.value = shortcut
    
    // Find matching shortcut
    const matchingAction = findMatchingAction(shortcut)
    if (matchingAction) {
      const handler = activeShortcuts.get(matchingAction)
      if (handler) {
        event.preventDefault()
        
        // Add to history
        addToHistory(matchingAction, shortcut)
        
        // Execute handler
        try {
          handler(event)
        } catch (error) {
          console.error('Keyboard shortcut error:', error)
        }
      }
    }
  }
  
  // Register a keyboard shortcut
  const register = (action, handler) => {
    if (typeof handler !== 'function') {
      console.warn(`Keyboard shortcut handler for '${action}' must be a function`)
      return
    }
    
    activeShortcuts.set(action, handler)
    
    return () => unregister(action)
  }
  
  // Unregister a keyboard shortcut
  const unregister = (action) => {
    activeShortcuts.delete(action)
  }
  
  // Clear all shortcuts
  const clear = () => {
    activeShortcuts.clear()
  }
  
  // Get shortcut for action
  const getShortcut = (action) => {
    return shortcuts.value[action] || null
  }
  
  // Get human-readable shortcut display
  const getShortcutDisplay = (shortcut) => {
    if (!shortcut) return ''
    
    const parts = shortcut.split('+')
    const displayParts = parts.map(part => {
      const displays = {
        'ctrl': '⌃',
        'cmd': '⌘',
        'alt': '⌥',
        'shift': '⇧',
        'enter': '↵',
        'esc': '⎋',
        'tab': '⇥',
        'space': '␣',
        'up': '↑',
        'down': '↓',
        'left': '←',
        'right': '→',
        'backspace': '⌫',
        'del': '⌦'
      }
      
      return displays[part] || part.toUpperCase()
    })
    
    return displayParts.join('')
  }
  
  // Initialize keyboard shortcuts
  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown, true)
  })
  
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown, true)
    clear()
  })
  
  return {
    // State
    shortcuts,
    isRecording,
    recordingFor,
    lastPressed,
    conflictingShortcuts,
    
    // Core methods
    register,
    unregister,
    clear,
    
    // Shortcut management
    getShortcut,
    getShortcutDisplay,
    
    // History
    history: shortcutHistory
  }
}